import * as React from "react";
import parse, {
  domToReact,
  Element,
  type DOMNode,
  type HTMLReactParserOptions,
} from "html-react-parser";

import { CitationUnderline } from "../../../components/CitationUnderline";
import { PdfSourcePreview } from "../../../components/PdfSourcePreview";
import { resolveCitationDocumentUrl } from "../../../services/gcpFileUrl";
import { EmbeddedIframePreview } from "./EmbeddedIframePreview";
import { getCitationColor } from "../colors";
import { normalizeSourceText } from "../content/sourceDisplay";

interface HtmlSourcePreviewProps {
  html: string;
  activeCitationId: string | null;
  title: string;
  sourceName?: string;
  iframeCitationColor?: string;
  filterSourceChrome?: boolean;
}

export const HtmlSourcePreview: React.FC<HtmlSourcePreviewProps> = ({
  html,
  activeCitationId,
  title,
  sourceName,
  iframeCitationColor,
  filterSourceChrome = false,
}) => {
  const parsed = React.useMemo(() => {
    let skippedDuplicateTitle = false;
    const articleTitle = getArticleTitle(title, sourceName);
    const normalizedTitle = normalizeSourceText(title);
    const normalizedArticleTitle = normalizeSourceText(articleTitle);
    const normalizedSourceNames = getSourceNameVariants(sourceName).map(normalizeSourceText);
    const options: HTMLReactParserOptions = {
      replace: (domNode) => {
        if (filterSourceChrome) {
          const noisyText = getSourceChromeText(domNode);
          if (noisyText) return <></>;
          const textNode = getSourceTextNode(domNode);
          if (
            textNode &&
            (normalizedTitle || normalizedArticleTitle) &&
            !skippedDuplicateTitle &&
            isDuplicateSourceTitle(
              textNode,
              normalizedTitle,
              normalizedArticleTitle,
              normalizedSourceNames
            )
          ) {
            skippedDuplicateTitle = true;
            return <></>;
          }
        }

        if (!(domNode instanceof Element)) return undefined;

        if (filterSourceChrome) {
          const elementText = normalizeSourceText(getElementTextContent(domNode));
          if (
            elementText &&
            (normalizedTitle || normalizedArticleTitle) &&
            !skippedDuplicateTitle &&
            isDuplicateSourceTitle(
              elementText,
              normalizedTitle,
              normalizedArticleTitle,
              normalizedSourceNames
            )
          ) {
            skippedDuplicateTitle = true;
            return <></>;
          }
          if (isSourceChromeElement(domNode)) return <></>;
          if (domNode.tagName === "h1") {
            const headingText = getElementDisplayText(domNode);
            if (headingText && articleTitle && isSameTextIgnoringSpacing(headingText, articleTitle)) {
              return <h1>{articleTitle}</h1>;
            }
          }
        }

        if (domNode.tagName === "u") {
          const attrs = domNode.attribs ?? {};
          const id = attrs.id ?? "";
          const dataSource = attrs["data-datasource"];
          const dataType = attrs["data-type"];
          const hasFinsterAttr = Boolean(id || dataSource || dataType);
          if (!hasFinsterAttr) return undefined;

          const citation = (
            <CitationUnderline id={id} dataSource={dataSource} dataType={dataType}>
              {domToReact(domNode.children as DOMNode[], options)}
            </CitationUnderline>
          );
          if (id && id === activeCitationId) {
            return (
              <span
                className="citation-html-active-target"
                data-active-citation="true"
                aria-label="Active citation"
              >
                {citation}
              </span>
            );
          }
          return citation;
        }

        if (domNode.tagName === "a") {
          const href = domNode.attribs?.href;
          if (!href) return undefined;
          return (
            <a href={resolveCitationDocumentUrl(href)} target="_blank" rel="noopener noreferrer">
              {domToReact(domNode.children as DOMNode[], options)}
            </a>
          );
        }

        if (domNode.tagName === "iframe") {
          const src = domNode.attribs?.src;
          if (!src) return <></>;
          const resolvedSrc = resolveCitationDocumentUrl(src);
          if (isPdfUrl(src)) {
            return (
              <div className="my-4 overflow-hidden rounded-md border border-border bg-background">
                <PdfSourcePreview
                  href={resolvedSrc}
                  title={title || "Highlighted PDF extract"}
                  highlights={[]}
                  initialHighlightId={activeCitationId}
                />
              </div>
            );
          }
          return (
            <div className="my-4 h-[520px] overflow-hidden rounded-md border border-border bg-muted/20">
              <EmbeddedIframePreview
                src={src}
                highlightUuid={activeCitationId}
                citationColor={
                  domNode.attribs?.["data-datasource"]
                    ? getCitationColor(domNode.attribs["data-datasource"])
                    : iframeCitationColor ?? getCitationColor(null)
                }
              />
            </div>
          );
        }

        return undefined;
      },
    };
    return parse(html, options);
  }, [activeCitationId, filterSourceChrome, html, iframeCitationColor, sourceName, title]);

  return <>{parsed}</>;
};

function getSourceChromeText(domNode: DOMNode): string | null {
  const text = getSourceTextNode(domNode);
  if (!text) return null;
  if (SOURCE_CHROME_TEXT.has(text)) return text;
  return null;
}

function getSourceTextNode(domNode: DOMNode): string | null {
  if (!("data" in domNode) || typeof domNode.data !== "string") return null;
  const normalized = normalizeSourceText(domNode.data);
  return normalized || null;
}

function getArticleTitle(title: string, sourceName: string | undefined): string {
  let out = title.trim();
  for (const variant of getSourceNameVariants(sourceName)) {
    out = stripSourcePrefix(out, variant);
    out = stripSourceSuffix(out, variant);
  }
  return out;
}

function isDuplicateSourceTitle(
  text: string,
  normalizedTitle: string,
  normalizedArticleTitle: string,
  normalizedSourceNames: string[]
): boolean {
  if (!text) return false;
  if (normalizedTitle) {
    if (text === normalizedTitle) return true;
    if (normalizedTitle.endsWith(text)) return true;
    if (text.endsWith(normalizedTitle)) return true;
  }
  if (normalizedArticleTitle) {
    if (text === normalizedArticleTitle) return true;
    if (
      normalizedSourceNames.some(
        (sourceName) => sourceName && text === `${normalizedArticleTitle} - ${sourceName}`
      )
    ) {
      return true;
    }
    if (text.endsWith(normalizedArticleTitle)) return true;
  }
  return false;
}

function getSourceNameVariants(sourceName: string | undefined): string[] {
  const trimmed = sourceName?.trim();
  if (!trimmed) return [];
  return Array.from(new Set([trimmed, stripTrailingParenthetical(trimmed)].filter(Boolean)));
}

function stripSourcePrefix(title: string, sourceName: string): string {
  const trimmed = title.trim();
  const prefix = `${sourceName}:`;
  return trimmed.toLowerCase().startsWith(prefix.toLowerCase())
    ? trimmed.slice(prefix.length).trim()
    : trimmed;
}

function stripSourceSuffix(title: string, sourceName: string): string {
  const trimmed = title.trim();
  const suffixStart = trimmed.toLowerCase().lastIndexOf(` - ${sourceName.toLowerCase()}`);
  if (suffixStart < 0) return trimmed;

  const suffixEnd = suffixStart + ` - ${sourceName}`.length;
  const remainder = trimmed.slice(suffixEnd).trim();
  if (remainder && stripTrailingParenthetical(remainder)) return trimmed;
  return trimmed.slice(0, suffixStart).trim();
}

function stripTrailingParenthetical(value: string): string {
  const trimmed = value.trim();
  if (!trimmed.endsWith(")")) return trimmed;
  const openIndex = trimmed.lastIndexOf("(");
  if (openIndex < 0) return trimmed;
  return trimmed.slice(0, openIndex).trim();
}

function isSourceChromeElement(domNode: Element): boolean {
  const attrs = domNode.attribs ?? {};
  if (attrs.hidden !== undefined || attrs["aria-hidden"] === "true") return true;
  const style = (attrs.style ?? "").toLowerCase();
  if (/\bdisplay\s*:\s*none\b/.test(style) || /\bvisibility\s*:\s*hidden\b/.test(style)) {
    return true;
  }
  const elementText = normalizeSourceText(getElementTextContent(domNode));
  if (isSourceChromeLabel(elementText)) return true;
  const className = `${attrs.class ?? ""} ${attrs.className ?? ""}`.toLowerCase();
  if (SOURCE_CHROME_CLASS_RE.test(className)) return true;
  return SOURCE_CHROME_CONTAINER_RE.test(className) && elementText.includes("share");
}

function getElementTextContent(domNode: DOMNode): string {
  if ("data" in domNode && typeof domNode.data === "string") return domNode.data;
  if (!("children" in domNode) || !Array.isArray(domNode.children)) return "";
  return (domNode.children as DOMNode[]).map(getElementTextContent).join(" ");
}

function getElementDisplayText(domNode: DOMNode): string {
  return getElementTextContent(domNode).replace(/\s+/g, " ").trim();
}

function isSameTextIgnoringSpacing(left: string, right: string): boolean {
  return compactComparableText(left) === compactComparableText(right);
}

function compactComparableText(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function isSourceChromeLabel(text: string): boolean {
  if (!text) return false;
  if (SOURCE_CHROME_TEXT.has(text)) return true;
  return text.length <= 60 && /^share(?: this)? article\b/.test(text);
}

const SOURCE_CHROME_TEXT = new Set([
  "skip to main content",
  "opens in new window",
  "open in new window",
  "opens new window",
  "source",
  "share article",
  "share this article",
  "visit link",
]);

const SOURCE_CHROME_CLASS_RE =
  /\b(sr-only|screen-reader-text|visually-hidden|visuallyhidden|a11y|assistive|accessible-text)\b/;
const SOURCE_CHROME_CONTAINER_RE = /\b(share|social|social-share|share-tools|share-wrapper)\b/;

function isPdfUrl(value: string): boolean {
  return /\.pdf(?:[?#]|$)/i.test(value);
}
